import styled from "styled-components";
import InputBox from "../../../Generic/InputBox";
import Textarea from "../../../Generic/Textarea";

export const Page = styled.div`
	width: 100%;
	display: flex;
	flex-direction: column;
	align-items: center;
	padding: 1.5rem;
`;

export const FormCard = styled.div`
	width: 100%;
	background: #fff;
	border-radius: 10px;
	padding: 2rem;
	box-shadow: rgba(0, 0, 0, 0.12) 0px 1px 3px, rgba(0, 0, 0, 0.24) 0px 1px 2px;
`;

export const CardHeader = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: center;
	width: 100%;
	padding-bottom: 1.5rem;
	border-bottom: 1px solid rgba(0, 0, 0, 0.1);
	margin-bottom: 1.5rem;
`;

export const CardTitle = styled.h2`
	font-size: 2rem;
	font-weight: 600;
	color: #36395a;
	margin: 0;
`;

export const Section = styled.div`
	display: flex;
	flex-direction: column;
	gap: 1.5rem;
	width: 100%;
	margin-bottom: 1.5rem;
`;

export const SectionTitle = styled.h3`
	font-size: 1.5rem;
	font-weight: 700;
	color: #36395a;
`;

export const FieldRow = styled.div`
	display: flex;
	flex-wrap: wrap;
	gap: 2rem;
	width: 100%;
`;

/** Standard control width for inputs/dropdowns — fits option labels without stretching full page. */
const FIELD_CONTROL_WIDTH = "25rem";

export const Field = styled.div`
	display: flex;
	flex-direction: column;
	gap: 0.5rem;
	flex: none;
	width: ${FIELD_CONTROL_WIDTH};
	max-width: 100%;
`;

export const FullWidthField = styled(Field)`
	width: 100%;
`;

export const FieldLabel = styled.label`
	font-size: 1rem;
	font-weight: 600;
	color: #36395a;
`;

export const FieldInput = styled(InputBox).attrs({ width: FIELD_CONTROL_WIDTH })`
	width: ${FIELD_CONTROL_WIDTH};
	box-sizing: border-box;
`;

export const FieldTextarea = styled(Textarea)`
	width: 100%;
`;

export const DropdownWrap = styled.div`
	width: 100%;

	& > div {
		box-shadow: none;
		border: 1px solid rgba(0, 0, 0, 1);
	}
`;

export const Footer = styled.div`
	display: flex;
	justify-content: flex-end;
	gap: 1rem;
	padding-top: 1.5rem;
	border-top: 1px solid rgba(0, 0, 0, 0.1);
	margin-top: 1rem;
`;

export const ErrorText = styled.span`
	color: red;
	font-size: 12px;
`;

export const SummaryPanel = styled.div`
	background: #f2f2f2;
	border-radius: 10px;
	padding: 1.5rem;
	display: flex;
	flex-direction: column;
	gap: 0.75rem;
`;

export const SummaryRow = styled.div`
	display: flex;
	justify-content: space-between;
	gap: 1rem;
	font-size: 0.9rem;
`;

export const SummaryLabel = styled.span`
	color: rgba(0, 0, 0, 0.6);
	font-weight: 500;
`;

export const SummaryValue = styled.span`
	color: #36395a;
	font-weight: 600;
	text-align: right;
`;

export const OverviewStack = styled.div`
	display: flex;
	flex-direction: column;
	gap: 1.25rem;
	width: 100%;
`;

export const OverviewIntro = styled.div`
	display: flex;
	flex-direction: column;
	gap: 0.35rem;
	margin-bottom: 0.25rem;
`;

export const OverviewTitle = styled.h3`
	margin: 0;
	font-size: 1.5rem;
	font-weight: 700;
	color: #36395a;
`;

export const OverviewSubtitle = styled.p`
	margin: 0;
	font-size: 0.95rem;
	color: rgba(0, 0, 0, 0.55);
`;

export const OverviewBanner = styled.div`
	display: flex;
	align-items: flex-start;
	gap: 0.5rem;
	padding: 0.75rem 1rem;
	border-radius: 8px;
	background: #f3f4f6;
	color: rgba(0, 0, 0, 0.7);
	font-size: 0.875rem;
	line-height: 1.4;
`;

export const OverviewCard = styled.section`
	border: 1px solid rgba(0, 0, 0, 0.12);
	border-radius: 12px;
	padding: 1.25rem 1.5rem;
	background: #fff;
	display: flex;
	flex-direction: column;
	gap: 1rem;
`;

export const OverviewCardHeader = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: center;
	gap: 1rem;
`;

export const OverviewCardTitle = styled.h4`
	margin: 0;
	font-size: 1.15rem;
	font-weight: 700;
	color: #36395a;
`;

export const OverviewEditLink = styled.button`
	border: none;
	background: none;
	padding: 0;
	color: #2563eb;
	font-size: 0.95rem;
	font-weight: 600;
	cursor: pointer;

	&:hover {
		text-decoration: underline;
	}
`;

export const OverviewFieldGrid = styled.div`
	display: grid;
	grid-template-columns: repeat(3, minmax(0, 1fr));
	gap: 1.25rem 1.5rem;

	@media (max-width: 900px) {
		grid-template-columns: repeat(2, minmax(0, 1fr));
	}

	@media (max-width: 600px) {
		grid-template-columns: 1fr;
	}
`;

export const OverviewField = styled.div`
	display: flex;
	flex-direction: column;
	gap: 0.3rem;
	min-width: 0;
`;

export const OverviewFieldLabel = styled.span`
	font-size: 0.8rem;
	font-weight: 700;
	color: rgba(0, 0, 0, 0.55);
`;

export const OverviewFieldValue = styled.span`
	font-size: 0.95rem;
	font-weight: 400;
	color: ${(props) => (props.$muted ? "rgba(0, 0, 0, 0.45)" : "#111827")};
	word-break: break-word;
`;

export const OverviewTable = styled.table`
	width: 100%;
	border-collapse: collapse;
	font-size: 0.9rem;

	th,
	td {
		text-align: left;
		padding: 0.65rem 0.5rem;
		border-bottom: 1px solid rgba(0, 0, 0, 0.08);
		vertical-align: top;
	}

	th {
		font-size: 0.8rem;
		font-weight: 700;
		color: rgba(0, 0, 0, 0.55);
	}

	td {
		color: #111827;
	}
`;

export const OverviewNote = styled.p`
	margin: 0;
	font-size: 0.8rem;
	color: rgba(0, 0, 0, 0.55);
`;

export const OverviewRemovedTag = styled.span`
	display: inline-block;
	margin-left: 0.5rem;
	padding: 0.1rem 0.45rem;
	border-radius: 999px;
	background: #fee2e2;
	color: #b91c1c;
	font-size: 0.7rem;
	font-weight: 700;
	vertical-align: middle;
`;

export const TagList = styled.div`
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	gap: 0.5rem;
`;

export const Tag = styled.span`
	background: rgba(45, 35, 66, 0.8);
	color: #fff;
	padding: 0.25rem 0.75rem;
	border-radius: 4px;
	font-size: 0.85rem;
	display: inline-flex;
	align-items: center;
	gap: 0.35rem;
`;

export const TagRemove = styled.button`
	background: none;
	border: none;
	color: #fff;
	cursor: pointer;
	padding: 0;
	font-size: 1rem;
	line-height: 1;
`;

export const TwoColumnLayout = styled.div`
	display: grid;
	grid-template-columns: 1fr 1fr;
	gap: 1.5rem;
	width: 100%;

	@media (max-width: 900px) {
		grid-template-columns: 1fr;
	}
`;
